/**
 * Serviço para geração de projetos com IA
 * Chama o endpoint local /api/arc_mosaico/projects/generate_with_meetings
 * O backend se encarrega de chamar o N8N e criar o projeto
 */
import axios from 'axios'

export interface AIProjectArea {
  id: string
  name: string
}

export interface AIProjectRequest {
  request: string          // Project description (from user input)
  gradeId: string          // Grade ID from /api/arc_mosaico/grades (converted to grade_id by axios)
  userPrompt: string       // User prompt (converted to user_prompt by axios)
  weeks: number            // Number of weeks
  daysPerWeek: number      // Days per week (converted to days_per_week by axios)
  dateStart: string        // Start date YYYY-MM-DD (converted to date_start by axios)
  // Optional fields
  duration?: number        // Duration in minutes (optional)
  areas?: AIProjectArea[]  // Selected areas/topics (optional)
}

export interface AIProjectResponse {
  success: boolean
  data?: {
    id: string
  }
  error?: string
}

export interface AskAIResponse {
  id: string
  status: 'processing' | 'completed' | 'failed'
  result?: {
    project_id: string
    metadata?: {
      [key: string]: unknown
    }
    [key: string]: unknown
  }
}

/**
 * Gera um projeto com meetings usando IA
 * POST /api/arc_mosaico/projects/generate_with_meetings
 * O backend chama o N8N e cria o projeto automaticamente
 */
export const generateProjectWithAI = async (payload: AIProjectRequest): Promise<AIProjectResponse> => {
  try {
    // Convert to snake_case manually to ensure correct format for backend
    // The axios interceptor may not work correctly with already snake_case keys
    const snakeCasePayload = {
      request: payload.request,
      grade_id: payload.gradeId,
      user_prompt: payload.userPrompt,
      weeks: payload.weeks,
      days_per_week: payload.daysPerWeek,
      date_start: payload.dateStart,
      duration: payload.duration,
      areas: payload.areas,
    }

    console.log('[AIProjectService] Sending payload:', snakeCasePayload)

    // Use axios directly to avoid double conversion by api interceptor
    const response = await axios.post('/api/arc_mosaico/projects/generate_with_meetings', snakeCasePayload, {
      timeout: 120000, // 2 minutos de timeout
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
      }
    })

    return {
      success: true,
      data: response.data
    }
  } catch (error: any) {
    console.error('[AIProjectService] Error:', error)

    if (error.response) {
      const errorData = error.response.data
      const errorMessage = errorData?.message || errorData?.error || error.response.statusText
      return {
        success: false,
        error: `API error: ${errorMessage}`
      }
    } else if (error.request) {
      return {
        success: false,
        error: 'No API response. Check your connection.'
      }
    } else {
      return {
        success: false,
        error: `Error: ${error.message}`
      }
    }
  }
}

/**
 * Consulta o status de um pedido de geração de projeto com IA
 * GET /api/projects/ask_ai/:id
 */
export const pollAskAI = async (requestId: string): Promise<AskAIResponse> => {
  const response = await axios.get(`/api/projects/ask_ai/${requestId}`, {
    headers: {
      'Accept': 'application/json',
      'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '',
    }
  })
  return response.data
}

/**
 * Formata a data para o formato esperado pela API (YYYY-MM-DD)
 */
export const formatDateForAPI = (date: Date | null): string => {
  if (!date) {
    return new Date().toISOString().split('T')[0]
  }
  return date.toISOString().split('T')[0]
}
